import React, { FC, useMemo } from 'react';

import { EvFlexColumn, EvSection, EvSpinner } from '@evinced-private/ui-common';

import {
	MFA_SESSION_ALL_ISSUES_TAB_DAID,
	MFA_SESSION_ISSUES_TABLE_DAID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import { useMfaSessionAllIssuesQuery } from 'src/apps/mfa/hooks/useMfaSessionAllIssuesQuery';
import { useMfaSessionScansQuery } from 'src/apps/mfa/hooks/useMfaSessionScansQuery';
import { MfaSingleSessionFiltersSection } from 'src/apps/mfa/pages/single-session/filters-section/MfaSingleSessionFiltersSection';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { MFA_SESSION_ALL_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { MFA_SESSION_ALL_ISSUES_LABELS } from './MfaSessionAllIssues.labels';
import { getMfaAllIssuesColumns } from './MfaSessionAllIssuesTableHelper';

interface IMfaSessionAllIssuesProps {
	sessionId: string;
}

export const MfaSessionAllIssues: FC<IMfaSessionAllIssuesProps> = ({ sessionId }) => {
	const { getTableStateById, onFilterChange } = useTableState();
	const tableId = useMemo(
		() => buildFullTableId(MFA_SESSION_ALL_ISSUES_TABLE_ID, sessionId),
		[sessionId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const issuesQuery = useMfaSessionAllIssuesQuery({
		sessionId,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filters
	});
	const scansQuery = useMfaSessionScansQuery({
		sessionId,
		filters
	});

	if (issuesQuery?.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_FLOW_ANALYZER}
			/>
		);
	}

	if (issuesQuery?.isLoading || scansQuery?.isLoading) {
		return <EvSpinner />;
	}

	const { total = 0, unfilteredTotal = 0, rows = [] } = issuesQuery.data ?? {};
	const scanCreatedAtMap = scansQuery.data ?? new Map<string, string>();
	const isTableLoading =
		issuesQuery.isLoading || issuesQuery.isPreviousData || scansQuery.isLoading;

	return (
		<EvSection dataAutomationId={MFA_SESSION_ALL_ISSUES_TAB_DAID}>
			<EvFlexColumn padding={['MEDIUM', 'NONE']}>
				<MfaSingleSessionFiltersSection
					sessionId={sessionId}
					filters={filters}
					ariaLabel={MFA_SESSION_ALL_ISSUES_LABELS.FILTERS_ARIA_LABEL}
					onFilterChange={onFilterChange}
					tableId={tableId}
				/>
			</EvFlexColumn>
			<EvSection
				ariaLabel={MFA_SESSION_ALL_ISSUES_LABELS.TABLE_ARIA_LABEL}
				skipLinkId="main-section"
			>
				<EvFlexColumn margin={['LARGE', 'NONE', 'NONE', 'NONE']}>
					<PlatformTable
						dataAutomationId={MFA_SESSION_ISSUES_TABLE_DAID}
						isTableLoading={isTableLoading}
						tableId={tableId}
						tableData={rows}
						totalCount={unfilteredTotal}
						totalPaginationableResults={total}
						persistTableState
						columns={getMfaAllIssuesColumns(sessionId, scanCreatedAtMap)}
						options={{
							keyField: 'id',
							title: MFA_SESSION_ALL_ISSUES_LABELS.TABLE_TITLE,
							caption: MFA_SESSION_ALL_ISSUES_LABELS.TABLE_CAPTION,
							pagination: true,
							dataType: MFA_SESSION_ALL_ISSUES_LABELS.ISSUES_DATA_TYPE,
							remote: true
						}}
						withUrlSwitcher={false}
					/>
				</EvFlexColumn>
			</EvSection>
		</EvSection>
	);
};
